import styled from "@emotion/styled"
import { Autocomplete, IconButton, TextField } from "@mui/material"
import { useQuery } from "@tanstack/react-query"
import {
  AutocompleteType,
  LocatorCitiesBody,
  Station,
  postDapiStationMapAutoComplete
} from "api"
import { AppType, useAppType } from "contexts/AppTypeProvider"
import { LocationContext } from "contexts/LocationProvider"
import { checkTwoArrayshaveCommonItems } from "lib/utils"
import { useContext, useEffect, useRef, useState } from "react"
import { trackMapSearch } from "reusable/ChargeGTM"
import { trackSearchBarStations } from "reusable/gtm"
import { CloseIconSmall, DropdownIcon, LocationIcon } from "ui"

type Props = {
  placeholder: string
  searchIcon: boolean
  width?: string
  height?: string
  radius?: string
  focusOnSpecificLocation: (lat: number, lon: number) => void
}

const StyledCloseIconSmall = styled(CloseIconSmall)`
  cursor: pointer;
  margin-right: 16px;
  width: 16px;
  height: 16px;
      ${(props) => props.theme.breakpoints.between("md", "lg")} {
      width: 11px;
      height: 11px
    }
`
const VerticalDashedLine = styled.div`
  width: 1px;
  height: 50%;
  border-right: 1px dashed rgba(185, 197, 210, 0.7);
  margin-right: 15px;
`;

const StyledInputBase = styled(TextField) <{ isTyping: boolean }>`
  color: black;
  font-size: 14px;
  line-height: 20px;
  height: 48px;
  & .MuiInputBase-root {
    height: 48px;
    padding-right: 5px !important;
    display: flex;
    align-items: center;
      input {
        padding-left: 10px;
      }
  }
  ${(props) => props.theme.breakpoints.between("xs", "md")} {
  & .MuiInputBase-root {
      padding: 0px;
        padding-left: 5px;
    }
  }
  ${(props) => props.theme.breakpoints.up("md")} {
    font-size: 12px;
    line-height: 18px;
    height: 38px;
    & .MuiInputBase-root {
      height: 38px;
      padding: 0px;
       padding-left: 5px;
      input {
        position: relative;
        padding-left: 10px;
      }
      svg {
        // padding-left: 5px;
      }
    }
  }
  ${(props) => props.theme.breakpoints.up("lg")} {
    font-size: 16px;
    line-height: 24px;
    height: 56px;
    & .MuiInputBase-root {
      padding-left: 5px;
      height: 56px;
      input {
        margin-bottom: 0px;
      }
    }
  }
  fieldset {
    border: 1px solid #153e694d !important;
    border-radius: 28px;
    ${(props) => (props.isTyping && 'box-shadow: 0px 0px 8px 0px rgba(130, 136, 148, 0.3);')}
    outline: none;
    :hover {
      border: none;
      border-radius: 28px;
      outline: none;
    }
  }
  & .MuiInputBase-input::placeholder {
    font-size: 14px;
    line-height: 20px;
    font-weight: 500;
    color: rgba(21, 62, 105, 1);
    ${(props) => props.theme.breakpoints.up("md")} {
      font-size: 12px;
      line-height: 18px;
    }
    ${(props) => props.theme.breakpoints.up("lg")} {
      font-size: 16px;
      line-height: 24px;
    }
  }
    ${(props) => props.theme.breakpoints.only("md")} {
  .MuiInputBase-root.MuiOutlinedInput-root {
    .MuiInputBase-input.MuiOutlinedInput-input.MuiAutocomplete-input {
      padding-top: 5px !important;  /* Example of overriding padding */
    }
  }
  }
`

const StyledIconButton = styled(IconButton)`
  :focus-visible{
    border: 1px solid gray;
    border-radius:0;
  }
  color: rgba(21, 62, 105, 1);
`
export const SearchInput: React.FC<Props> = ({ placeholder, focusOnSpecificLocation }) => {
  const [interim, setInterim] = useState("")
  const [isFocused, setIsFocused] = useState(false)
  const [firstSearchValue, setFirstSearchValue] = useState(false)
  const [isUserTyping, setIsUserTyping] = useState(false)
  const inputRef = useRef<HTMLInputElement>(null)
  const { appType } = useAppType()
  const { searchValue, setSearchValue, setSelectedCity, selectedCity, setSelectedLocation, selectedLocation, filters } =
    useContext(LocationContext)

  useEffect(() => {
    const timer = setTimeout(() => {
      if (appType === AppType.Corporate) {
        trackMapSearch(interim)
      }
      setSearchValue(interim)
    }, 500) // 1000 milliseconds = 1 second
    return () => clearTimeout(timer)
  }, [interim, setSearchValue])

  useEffect(() => {
    if (searchValue && !firstSearchValue) {
      setFirstSearchValue(true)
      setInterim(searchValue)
    }
  }, [searchValue, firstSearchValue])

  useEffect(() => {
    inputRef.current?.setAttribute("aria-label", "חיפוש עיר, כתובת")
  }, [])

  const { data } = useQuery<LocatorCitiesBody>({
    queryKey: ["searchValue", searchValue],
    queryFn: async () => {
      const res = await postDapiStationMapAutoComplete("", {
        Term: searchValue,
        LocationLat: 0,
        LocationLng: 0,
        MaxResults: 50,
      })
      const { cities, stations: allStation } = res.data.Body.body
      const metadataIdsFromFilter = filters?.map(filter => filter.id) || []
      const stations = metadataIdsFromFilter.length > 0 ? allStation.filter((station: Station) => {
        const metadataIds = station.metaData.map(meta => meta.id)
        return checkTwoArrayshaveCommonItems(metadataIds, metadataIdsFromFilter)
      }) : allStation

      return { cities, stations }
    },
    enabled: !!searchValue,
  })
  const options = [
    ...(data?.cities?.map((city) => ({
      ...city,
      type: AutocompleteType.City,
      geoLocation: undefined,
    })) || []),
    ...(data?.stations?.map((station) => ({
      ...station,
      type: AutocompleteType.Station,
      geoLocation: station.geoLocation,
    })) || []),
  ]

  const getPlaceholder = () => {
    if (!isFocused) {
      if (searchValue) {
        return searchValue
      } else if (placeholder) {
        return placeholder
      } else {
        return ""
      }
    }
  }

  const clearInput = () => {
    setSelectedCity(null)
    setSelectedLocation(null)
    setInterim("")
    setIsUserTyping(false)
    inputRef.current?.blur()
  }

  const handleInputChange = (e: any) => {
    setSelectedCity(null)
    setSelectedLocation(null)
    setInterim(e.target.value)
    setIsUserTyping(true)
  }
  const getCurrentLocation = () => {
    const location = options.find((el) => el.id === selectedLocation?.id)
    const city = options.find((el) => el.id === selectedCity?.id)
    return location || city
  }
  const currentLocation = getCurrentLocation()

  return (
    <Autocomplete
      fullWidth={true}
      options={options}
      aria-label="חיפוש עיר, כתובת"
      sx={{
        "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
          border: "none",
        },
        "&.MuiAutocomplete-root": {
          paddingLeft: 0
        }
      }}
      getOptionLabel={(option) => option.name}
      groupBy={(option) => option.type}
      inputValue={interim}
      value={currentLocation}
      filterOptions={(optionsResults) => optionsResults}
      onChange={(_, value) => {
        if (value?.type === AutocompleteType.City) {
          trackSearchBarStations(value.name)
          setSelectedCity({
            id: value.id,
            type: AutocompleteType.City,
            name: value.name,
          })
          setInterim(value.name)
          return
        }
        if (value?.type === AutocompleteType.Station) {
          trackSearchBarStations(value.name)
          const typedValue: any = value
          const station: Station = typedValue
          setSelectedLocation(station)
          focusOnSpecificLocation(station.geoLocation?.latitude!, station.geoLocation?.longitude!)
          setInterim(value.name)
          return
        }
      }}
      popupIcon={<DropdownIcon />}
      renderInput={(params) => (
        <StyledInputBase
          {...params}
          isTyping={isUserTyping}
          inputRef={inputRef}
          placeholder={getPlaceholder()}
          onFocus={() => setIsFocused(true)}
          onBlur={() => {
            setIsUserTyping(false)
            setIsFocused(false)
          }
          }
          focused={isFocused}
          variant="outlined"
          value={interim}
          onChange={handleInputChange}
          InputProps={{
            ...params.InputProps,
            "aria-label": "חיפוש עיר, כתובת",
            startAdornment: <LocationIcon color="rgba(21, 62, 105, 1)" width={30} />,
            endAdornment: <>
              {
                isUserTyping &&
                <>
                  <StyledCloseIconSmall onClick={() => clearInput()}></StyledCloseIconSmall>
                  <VerticalDashedLine />
                </>
              }
              <StyledIconButton
                disableRipple
                type="submit"
                style={{
                  padding: 0,
                  margin: 0,
                  minHeight: 0,
                  minWidth: 0,
                }}
                color="primary"
                sx={{ p: "0px" }}
                // tabIndex={-1}
                aria-label="חפש תחנה או מיקום"
              >
                {/* <Mobile>
                  <SubmitSearchIcon blue width={32} height={32} />
                </Mobile>

                <Desktop>
                  <SubmitSearchIcon blue width={isLDesktop ? 45 : 27} />
                </Desktop> */}
              </StyledIconButton>
            </>
          }}
        />
      )}
    />
  )
}
